'use client';

import {atom, useSetAtom} from 'jotai';
import {useAtomCallback} from 'jotai/react/utils';
import {useCallback} from 'react';

import AllCategoryIcon from '@web-catalog/assets/icons/all_categories_outline_24.svg';
import SparkleIcon from '@web-catalog/assets/icons/sparkle_24.svg';

import {
  ALL_APPS_TITLE,
  ALL_GAMES_TITLE,
  INTERESTING_TITLE,
} from '@web-catalog/const';

import {AppCategory, AppType, NO_CATEGORY_CATEGORY} from '@web-catalog/types';

import {Scroller} from '@web-catalog/ui-kit/Scroller';

import {useCategoriesAnalytics} from '@web-catalog/utils/analytics/useCategoriesAnalytics';

import styles from './Categories.module.scss';
import {Chip} from './Chip';
import {useSplitCategoriesToChunks} from './useSplitCategoriesToChunks';

type Props = {
  appType: AppType;
  activeCategory: string | null;
  categories: AppCategory[];
};

const getLink = (category: string, appType: AppType) =>
  `${appType === AppType.GAMES ? '/games' : ''}/${category}`;

// для восстановления позиции при переключении категорий
// TODO после конца аб мб вынести список категорий и пр. в layout.tsx, тогда этим не надо будет заниматься
const scrollOffsetDataAtom = atom<{offset: number; appType: AppType}>({
  offset: 0,
  appType: AppType.MAIN,
});

export const Categories = ({categories, appType, activeCategory}: Props) => {
  const setScrollOffsetData = useSetAtom(scrollOffsetDataAtom);
  const logger = useCategoriesAnalytics('bubble');

  const storeScrollOffset = useCallback(
    (offset: number) => setScrollOffsetData({appType, offset}),
    [appType, setScrollOffsetData],
  );

  const getStoredScrollOffset = useAtomCallback(
    useCallback(
      (get) => {
        const value = get(scrollOffsetDataAtom);
        if (value.appType === appType) {
          return value.offset;
        }
        return 0;
      },
      [appType],
    ),
  );

  const categoryChunks = useSplitCategoriesToChunks(categories, appType);

  const renderCategory = (category: AppCategory) =>
    category.appCount === 0 ? null : (
      <Chip
        key={category.category}
        linkTo={getLink(category.category.toLowerCase(), appType)}
        label={category.categoryName}
        iconSrc={category.iconUrl}
        iconColorVariant={category.iconColor}
        active={activeCategory === category.category}
        onClick={(active) => {
          appType === AppType.GAMES
            ? logger.logGameCategorySelect(category.category, active)
            : logger.logAppCategorySelect(category.category, active);
        }}
        testId="CategoryStringLink"
      />
    );

  return (
    <Scroller
      navButtonsScrollBy={300}
      getStoredScrollOffset={getStoredScrollOffset}
      storeScrollOffset={storeScrollOffset}
      classes={{
        root: styles.scrollableRoot,
        scrollable: styles.scrollableContainer,
        prevNavButton: styles.scrollableNavButtons,
        nextNavButton: styles.scrollableNavButtons,
      }}
      disableDefaultScrollableContainerClass
      testId="categories"
    >
      {categoryChunks.map((chunk, index) => (
        <span className={styles.row} key={index}>
          {index === 0 && (
            <>
              <Chip
                linkTo={appType === AppType.GAMES ? '/games' : '/'}
                label={INTERESTING_TITLE}
                iconComponent={SparkleIcon}
                active={!activeCategory}
                onClick={(active) => {
                  appType === AppType.GAMES
                    ? logger.logGameInterestingSelect(active)
                    : logger.logAppInterestingSelect(active);
                }}
              />
              <Chip
                linkTo={getLink(NO_CATEGORY_CATEGORY, appType)}
                label={
                  appType === AppType.GAMES ? ALL_GAMES_TITLE : ALL_APPS_TITLE
                }
                iconComponent={AllCategoryIcon}
                active={activeCategory === NO_CATEGORY_CATEGORY}
                onClick={(active) => {
                  appType === AppType.GAMES
                    ? logger.logAllGamesClick(active)
                    : logger.logAllAppsClick(active);
                }}
                testId="CategoryStringLink"
              />
            </>
          )}
          {chunk.map(renderCategory)}
        </span>
      ))}
    </Scroller>
  );
};
